import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDetail } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, StatusBadge } from "@/components/ui-parts";
import { UserPlus, Trash2, Copy, Check, Users, X, Pencil, MessageSquare, Bot, ExternalLink, KeyRound } from "lucide-react";
import { toast } from "sonner";

const roleLabels = { owner: "Inhaber", accountant: "Buchhalter", entry: "Erfasser" };
const roleDescriptions = {
  accountant: "Alles außer Nutzer verwalten",
  entry: "Nur Belege hochladen und ansehen",
};

function CompanyPicker({ companies, scope, setScope, selected, setSelected }) {
  const toggle = (id) =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  return (
    <div>
      <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Firmen-Zugriff</label>
      <div className="flex items-center gap-4 text-sm">
        <label className="inline-flex items-center gap-2">
          <input type="radio" checked={scope === "all"} onChange={() => setScope("all")} data-testid="invite-scope-all" />
          Alle Firmen
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="radio" checked={scope === "custom"} onChange={() => setScope("custom")} data-testid="invite-scope-custom" />
          Nur ausgewählte
        </label>
      </div>
      {scope === "custom" && (
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {companies.map((c) => (
            <label
              key={c.id}
              className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer text-sm transition-colors ${
                selected.includes(c.id) ? "border-[#18322B] bg-[#E4F0E8]" : "border-[#E6E6E4]"
              }`}
            >
              <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} />
              <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
              <span className="text-[#1A1A1A] truncate">{c.name}</span>
            </label>
          ))}
          {companies.length === 0 && <div className="text-xs text-[#5C5E60]">Noch keine Firmen angelegt.</div>}
        </div>
      )}
    </div>
  );
}

export default function Team() {
  const [members, setMembers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [newInvite, setNewInvite] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(null); // membership doc while editing

  // Form (invite)
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("entry");
  const [scope, setScope] = useState("all");
  const [selectedCompanies, setSelectedCompanies] = useState([]);

  // Form (edit)
  const [ePhone, setEPhone] = useState("");
  const [eName, setEName] = useState("");
  const [eEmail, setEEmail] = useState("");
  const [eScope, setEScope] = useState("all");
  const [eCompanies, setECompanies] = useState([]);
  const [ePerms, setEPerms] = useState({}); // bot_permissions map
  const [permSchema, setPermSchema] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [t, c, s] = await Promise.all([
        api.get("/team"),
        api.get("/companies"),
        api.get("/team/bot-permissions/schema").catch(() => ({ data: [] })),
      ]);
      setMembers(t.data);
      setCompanies(c.data);
      setPermSchema(s.data || []);
    } catch {
      toast.error("Konnte Team nicht laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setEmail("");
    setName("");
    setPhone("");
    setRole("entry");
    setScope("all");
    setSelectedCompanies([]);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        email,
        name,
        role,
        phone: phone.trim() || null,
        allowed_company_ids: scope === "all" ? [] : selectedCompanies,
      };
      const { data } = await api.post("/team/invite", payload);
      setNewInvite(data);
      setShowInvite(false);
      resetForm();
      load();
      toast.success(data.status === "active" ? "Mitarbeiter hinzugefügt" : "Einladung erstellt");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    }
  };

  const updateMemberInline = async (m, updates) => {
    try {
      await api.patch(`/team/${m.id}`, updates);
      toast.success("Aktualisiert");
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const openEdit = (m) => {
    setEditing(m);
    setEPhone(m.phone || "");
    setEName(m.name || "");
    setEEmail(m.email || "");
    const all = (m.allowed_company_ids || []).length === 0;
    setEScope(all ? "all" : "custom");
    setECompanies(m.allowed_company_ids || []);
    // Seed bot permissions from backend response, fallback to schema defaults.
    const seeded = {};
    (permSchema || []).forEach((p) => {
      seeded[p.key] = m.bot_permissions ? !!m.bot_permissions[p.key] : !!p.default;
    });
    setEPerms(seeded);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    try {
      const payload = {
        name: eName,
        phone: ePhone.trim(),
        allowed_company_ids: eScope === "all" ? [] : eCompanies,
        bot_permissions: ePerms,
      };
      const trimmedEmail = (eEmail || "").trim().toLowerCase();
      if (trimmedEmail && trimmedEmail !== (editing.email || "").toLowerCase()) {
        payload.email = trimmedEmail;
      }
      await api.patch(`/team/${editing.id}`, payload);
      toast.success("Mitarbeiter aktualisiert");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Zugang für ${m.email} entziehen?`)) return;
    try {
      await api.delete(`/team/${m.id}`);
      toast.success("Zugang entzogen");
      load();
    } catch {
      toast.error("Fehlgeschlagen");
    }
  };

  const [resetTarget, setResetTarget] = React.useState(null);          // member being reset
  const [resetResult, setResetResult] = React.useState(null);          // {email, password, was_new}
  const [resetPassword, setResetPassword] = React.useState("");        // manual password input
  const [resetBusy, setResetBusy] = React.useState(false);
  const [resetCopied, setResetCopied] = React.useState(false);

  const openReset = (m) => {
    setResetTarget(m);
    setResetResult(null);
    setResetPassword("");
    setResetCopied(false);
  };

  const submitReset = async (mode) => {
    if (!resetTarget) return;
    if (mode === "manual" && (resetPassword || "").length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben");
      return;
    }
    setResetBusy(true);
    try {
      const body = mode === "manual" ? { password: resetPassword } : { generate: true };
      const { data } = await api.post(`/team/${resetTarget.id}/set-password`, body);
      setResetResult(data);
      toast.success(data.was_new ? "Account angelegt & Passwort gesetzt" : "Passwort neu gesetzt");
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Fehlgeschlagen");
    } finally {
      setResetBusy(false);
    }
  };

  const copyCreds = () => {
    if (!resetResult) return;
    const text = `E-Mail: ${resetResult.email}\nPasswort: ${resetResult.password}\n\nAnmeldung: ${window.location.origin}/login`;
    navigator.clipboard.writeText(text);
    setResetCopied(true);
    setTimeout(() => setResetCopied(false), 2500);
  };

  const closeReset = () => {
    setResetTarget(null);
    setResetResult(null);
    setResetPassword("");
    setResetCopied(false);
  };

  const copyInviteLink = () => {
    if (!newInvite?.invite_token) return;
    const link = `${window.location.origin}/register?token=${newInvite.invite_token}&email=${encodeURIComponent(newInvite.email)}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <PageHeader
        title="Mitarbeiter"
        subtitle="Team einladen, Rollen zuweisen und Firmen-Zugriffe steuern."
        actions={
          !showInvite && (
            <PrimaryButton onClick={() => setShowInvite(true)} data-testid="invite-member-btn">
              <UserPlus className="h-4 w-4" /> Mitarbeiter einladen
            </PrimaryButton>
          )
        }
      />

      <div className="px-8 py-8 space-y-6">
        {newInvite && (
          <div className="bg-[#E4F0E8] border border-[#18322B]/20 rounded-md p-5" data-testid="invite-created">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-base text-[#18322B]">
                {newInvite.status === "active" ? "Direkt verknüpft" : "Einladung erstellt"}
              </div>
              <button onClick={() => setNewInvite(null)} className="text-[#18322B]">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="text-xs text-[#1E4620] mt-1">
              {newInvite.status === "active"
                ? `${newInvite.email} hat bereits ein vorbuch.de-Konto und wurde direkt hinzugefügt.`
                : `Senden Sie ${newInvite.email} den folgenden Einladungslink zur Registrierung.`}
            </div>
            {newInvite.invite_token && newInvite.status === "pending" && (
              <div className="mt-3 flex items-center gap-2 bg-white border border-[#E6E6E4] rounded-md px-3 py-2 font-mono text-xs">
                <span className="flex-1 truncate select-all" data-testid="invite-link">
                  {window.location.origin}/register?token={newInvite.invite_token}&amp;email={encodeURIComponent(newInvite.email)}
                </span>
                <button
                  onClick={copyInviteLink}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-[#18322B] text-white hover:bg-[#21453B]"
                >
                  {copied ? (
                    <>
                      <Check className="h-3.5 w-3.5" /> Kopiert
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" /> Kopieren
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {showInvite && (
          <form onSubmit={submit} className="bg-white border border-[#E6E6E4] rounded-md p-6 space-y-4" data-testid="invite-form">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-lg text-[#1A1A1A]">Mitarbeiter einladen</div>
              <button
                type="button"
                onClick={() => {
                  setShowInvite(false);
                  resetForm();
                }}
                className="text-[#5C5E60] hover:text-[#1A1A1A]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">E-Mail *</label>
                <input
                  required
                  type="email"
                  data-testid="invite-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B]"
                  placeholder="kollegin@firma.de"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Name (optional)</label>
                <input
                  value={name}
                  data-testid="invite-name"
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B]"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">
                Telefonnummer <span className="text-[#5C5E60] font-normal">(für Telegram-Freischaltung)</span>
              </label>
              <input
                data-testid="invite-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B]"
                placeholder="+49 170 1234567"
              />
              <div className="text-xs text-[#5C5E60] mt-1">
                Nur Nummern, die hier hinterlegt sind, dürfen dem Bot Belege senden.
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Rolle</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {["accountant", "entry"].map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRole(r)}
                    data-testid={`invite-role-${r}`}
                    className={`text-left px-4 py-3 border rounded-md transition-colors duration-150 ${
                      role === r ? "border-[#18322B] bg-[#E4F0E8]" : "border-[#E6E6E4] hover:bg-[#F9F9F8]"
                    }`}
                  >
                    <div className="text-sm font-semibold text-[#1A1A1A]">{roleLabels[r]}</div>
                    <div className="text-xs text-[#5C5E60] mt-0.5">{roleDescriptions[r]}</div>
                  </button>
                ))}
              </div>
            </div>

            <CompanyPicker
              companies={companies}
              scope={scope}
              setScope={setScope}
              selected={selectedCompanies}
              setSelected={setSelectedCompanies}
            />

            <div className="flex justify-end gap-2 pt-2">
              <SecondaryButton
                type="button"
                onClick={() => {
                  setShowInvite(false);
                  resetForm();
                }}
              >
                Abbrechen
              </SecondaryButton>
              <PrimaryButton type="submit" data-testid="invite-submit">
                Einladen
              </PrimaryButton>
            </div>
          </form>
        )}

        {editing && (
          <form onSubmit={submitEdit} className="bg-white border border-[#18322B] rounded-md p-6 space-y-4" data-testid="edit-form">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-lg text-[#1A1A1A]">
                {editing.name || editing.email} bearbeiten
              </div>
              <button type="button" onClick={() => setEditing(null)} className="text-[#5C5E60] hover:text-[#1A1A1A]">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Name</label>
                <input
                  value={eName}
                  data-testid="edit-name"
                  onChange={(e) => setEName(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Telefonnummer</label>
                <input
                  value={ePhone}
                  data-testid="edit-phone"
                  onChange={(e) => setEPhone(e.target.value)}
                  placeholder="+49 170 1234567"
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]"
                />
                {editing.telegram_linked && (
                  <div className="text-[11px] text-[#0F5132] mt-1">
                    Bereits mit Telegram @{editing.telegram_username || editing.telegram_user_id} verknüpft.
                  </div>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">
                  E-Mail (Login)
                </label>
                <input
                  type="email"
                  value={eEmail}
                  data-testid="edit-email"
                  onChange={(e) => setEEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono focus:outline-none focus:border-[#18322B]"
                />
                <div className="text-[11px] text-[#5C5E60] mt-1">
                  Wenn der Mitarbeiter bereits einen Login hat, wird die Anmelde-E-Mail automatisch mit umgestellt.
                  Danach kann er sich mit der neuen Adresse und seinem bisherigen Passwort einloggen.
                </div>
              </div>
            </div>
            <CompanyPicker
              companies={companies}
              scope={eScope}
              setScope={setEScope}
              selected={eCompanies}
              setSelected={setECompanies}
            />

            {permSchema.length > 0 && (
              <div className="pt-3 border-t border-[#E6E6E4]">
                <div className="flex items-center gap-2 mb-3">
                  <Bot className="h-4 w-4 text-[#18322B]" strokeWidth={2} />
                  <div className="font-display font-bold text-sm text-[#1A1A1A]">
                    Bot-Berechtigungen
                  </div>
                </div>
                <p className="text-xs text-[#5C5E60] mb-3">
                  Steuere, welche Informationen der Bot mit diesem Mitarbeiter teilen darf.
                  Sensible Rechte (rot) sind standardmäßig deaktiviert.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="bot-perms-grid">
                  {permSchema.map((p) => {
                    const on = !!ePerms[p.key];
                    return (
                      <label
                        key={p.key}
                        className={`flex items-start gap-2 px-3 py-2 border rounded-md cursor-pointer text-xs transition-colors ${
                          on
                            ? "border-[#18322B] bg-[#E4F0E8]"
                            : "border-[#E6E6E4] bg-white"
                        } ${p.sensitive ? "border-l-4 border-l-[#B31D1D]" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={(ev) =>
                            setEPerms((prev) => ({ ...prev, [p.key]: ev.target.checked }))
                          }
                          data-testid={`perm-${p.key}`}
                          className="mt-0.5"
                        />
                        <div className="flex-1">
                          <div className="text-[#1A1A1A] font-semibold">
                            {p.label}
                            {p.sensitive && (
                              <span className="ml-1 text-[10px] uppercase tracking-wider text-[#B31D1D]">
                                sensibel
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-[#5C5E60] mt-0.5">{p.description}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <SecondaryButton type="button" onClick={() => setEditing(null)}>
                Abbrechen
              </SecondaryButton>
              <PrimaryButton type="submit" data-testid="edit-submit">
                Speichern
              </PrimaryButton>
            </div>
          </form>
        )}

        {loading ? (
          <div className="text-sm text-[#5C5E60]">Lade...</div>
        ) : (
          <div className="bg-white border border-[#E6E6E4] rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="team-list">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] bg-[#F9F9F8] border-b border-[#E6E6E4]">
                  <th className="text-left px-6 py-3 font-semibold">Mitarbeiter</th>
                  <th className="text-left px-4 py-3 font-semibold">Telefon / Telegram</th>
                  <th className="text-left px-4 py-3 font-semibold">Rolle</th>
                  <th className="text-left px-4 py-3 font-semibold">Firmen-Zugriff</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-6 py-3 font-semibold">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr
                    key={m.id}
                    className="border-b border-[#E6E6E4] last:border-b-0 hover:bg-[#F9F9F8] transition-colors duration-150"
                  >
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-[#5C5E60]" strokeWidth={1.75} />
                        <div>
                          <div className="font-medium text-[#1A1A1A]">{m.name || m.email}</div>
                          <div className="text-xs text-[#5C5E60]">{m.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {m.is_owner ? (
                        <span className="text-[#5C5E60]">—</span>
                      ) : (
                        <div className="space-y-0.5">
                          <div className="text-[#1A1A1A] font-mono-tab">{m.phone || <span className="text-[#5C5E60]">keine Nummer</span>}</div>
                          {m.telegram_linked ? (
                            <div className="inline-flex items-center gap-1 text-[10px] text-[#0F5132]">
                              <MessageSquare className="h-3 w-3" strokeWidth={2} />
                              Telegram aktiv{m.telegram_username ? ` · @${m.telegram_username}` : ""}
                            </div>
                          ) : m.phone ? (
                            <div className="text-[10px] text-[#5C5E60]">Warte auf Bot-Verknüpfung</div>
                          ) : null}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {m.is_owner ? (
                        <StatusBadge variant="success">{roleLabels.owner}</StatusBadge>
                      ) : (
                        <select
                          value={m.role}
                          onChange={(e) => updateMemberInline(m, { role: e.target.value })}
                          data-testid={`role-${m.id}`}
                          className="text-xs px-2 py-1 border border-[#E6E6E4] rounded-md bg-white hover:border-[#18322B]"
                        >
                          <option value="accountant">Buchhalter</option>
                          <option value="entry">Erfasser</option>
                        </select>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#5C5E60] text-xs">
                      {m.is_owner
                        ? "Alle Firmen"
                        : (m.allowed_company_ids || []).length === 0
                        ? "Alle Firmen"
                        : `${m.allowed_company_ids.length} Firma${m.allowed_company_ids.length > 1 ? "en" : ""}`}
                    </td>
                    <td className="px-4 py-3">
                      {m.status === "active" ? (
                        <StatusBadge variant="success">Aktiv</StatusBadge>
                      ) : m.status === "pending" ? (
                        <StatusBadge variant="warning">Ausstehend</StatusBadge>
                      ) : (
                        <StatusBadge variant="error">Widerrufen</StatusBadge>
                      )}
                    </td>
                    <td className="text-right px-6 py-3">
                      {!m.is_owner && m.status !== "revoked" && (
                        <div className="inline-flex items-center gap-1">
                          <Link
                            to={`/app/einstellungen/mitarbeiter/${m.id}`}
                            data-testid={`details-${m.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#F3F3F1] text-[#1A1A1A] transition-colors duration-150"
                          >
                            <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} /> Details
                          </Link>
                          <button
                            onClick={() => openEdit(m)}
                            data-testid={`edit-${m.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#F3F3F1] text-[#1A1A1A] transition-colors duration-150"
                          >
                            <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} /> Bearbeiten
                          </button>
                          <button
                            onClick={() => openReset(m)}
                            data-testid={`reset-password-${m.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#EAF4FA] text-[#1A4971] transition-colors duration-150"
                          >
                            <KeyRound className="h-3.5 w-3.5" strokeWidth={1.75} /> Passwort
                          </button>
                          <button
                            onClick={() => remove(m)}
                            data-testid={`remove-${m.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#FCE8E8] text-[#B31D1D] transition-colors duration-150"
                          >
                            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} /> Entfernen
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Password reset modal */}
      {resetTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          data-testid="reset-password-modal"
          onClick={closeReset}
        >
          <div
            className="bg-white rounded-lg border border-[#E6E6E4] w-full max-w-md p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5E60] font-semibold">Zugangsdaten</div>
                <div className="font-display font-bold text-lg text-[#1A1A1A]">
                  {resetResult ? "Neues Passwort" : "Passwort setzen"}
                </div>
                <div className="text-xs text-[#5C5E60] mt-0.5 font-mono">{resetTarget.email}</div>
              </div>
              <button onClick={closeReset} className="text-[#5C5E60] hover:text-[#1A1A1A]" data-testid="reset-close">
                <X className="h-4 w-4" />
              </button>
            </div>

            {!resetResult ? (
              <>
                <div className="text-xs text-[#5C5E60] mb-4 leading-relaxed">
                  Setze für <b>{resetTarget.name || resetTarget.email}</b> ein neues Passwort. Der Mitarbeiter kann sich damit sofort mit seiner E-Mail-Adresse anmelden.
                  {!resetTarget.user_id && (
                    <div className="mt-2 rounded bg-[#FFF4E5] border border-[#F0D4A5] p-2 text-[#915000]">
                      Dieser Mitarbeiter hat noch <b>keinen</b> Login-Account – er wird automatisch mit angelegt.
                    </div>
                  )}
                </div>

                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Eigenes Passwort</label>
                <input
                  type="text"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  placeholder="min. 6 Zeichen"
                  data-testid="reset-password-input"
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono focus:outline-none focus:border-[#18322B] mb-3"
                />
                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={() => submitReset("manual")}
                    disabled={resetBusy || (resetPassword || "").length < 6}
                    data-testid="reset-submit-manual"
                  >
                    {resetBusy ? "…" : "Passwort speichern"}
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => submitReset("generate")}
                    disabled={resetBusy}
                    data-testid="reset-submit-generate"
                  >
                    {resetBusy ? "…" : "Zufälliges generieren"}
                  </SecondaryButton>
                </div>
              </>
            ) : (
              <>
                <div className="text-xs text-[#5C5E60] mb-3 leading-relaxed">
                  {resetResult.was_new
                    ? "Account wurde angelegt. Gib diese Zugangsdaten dem Mitarbeiter weiter – sie werden aus Sicherheitsgründen nicht erneut angezeigt."
                    : "Passwort wurde geändert. Gib diese Zugangsdaten dem Mitarbeiter weiter – sie werden aus Sicherheitsgründen nicht erneut angezeigt."}
                </div>
                <div className="bg-[#F9F9F8] border border-[#E6E6E4] rounded-md p-3 space-y-2 mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5C5E60] font-semibold">E-Mail</div>
                    <div className="font-mono text-sm text-[#1A1A1A]" data-testid="reset-result-email">{resetResult.email}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5C5E60] font-semibold">Passwort</div>
                    <div className="font-mono text-sm text-[#1A1A1A] break-all" data-testid="reset-result-password">{resetResult.password}</div>
                  </div>
                </div>
                <button
                  onClick={copyCreds}
                  data-testid="reset-copy-creds"
                  className="inline-flex items-center gap-2 w-full justify-center bg-[#18322B] text-white text-sm px-4 py-2 rounded-md hover:bg-[#21453B]"
                >
                  {resetCopied ? (
                    <><Check className="h-4 w-4" /> Kopiert</>
                  ) : (
                    <><Copy className="h-4 w-4" /> Zugangsdaten kopieren</>
                  )}
                </button>
                <button
                  onClick={closeReset}
                  className="w-full mt-2 text-xs text-[#5C5E60] hover:text-[#1A1A1A] py-1"
                >
                  Schließen
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
